import React from 'react';
import { markupIssues } from '../markupCheck';
import { modeSelection as selectionOf, wantsMachineTranslation, isSkippedByDefault, skipReasonOf, referenceReasonOf, matchesSelection } from '../machineTranslationRules';
import Box from '@mui/material/Box';
import { isShopImageRef } from '../utils/shopImages';
import { probeImage, imageProbeResult } from '../utils/imageProbe';
import { bannerSurface } from '../theme';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import withStyles from '../withStylesAdapter';
import withRouter from 'with-router'
import { bindActionCreators } from 'redux'
import { connect } from 'react-redux';
import TextField from '@mui/material/TextField';
import Divider from '@mui/material/Divider';
import Collapse from '@mui/material/Collapse';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import InputBase from '@mui/material/InputBase';
import ChevronRightOutlined from '@mui/icons-material/ChevronRightOutlined';
import { WindowScroller, CellMeasurer, CellMeasurerCache, List, AutoSizer } from 'react-virtualized';
import 'react-virtualized/styles.css'; // only needs to be imported once
import { isImageSource } from '../utils/shopImages';
import CircularProgress from '@mui/material/CircularProgress';
import TranslateOutlined from '@mui/icons-material/TranslateOutlined';
import CloseIcon from '@mui/icons-material/Close';
import Sticky from 'react-sticky-el';
import FormGroup from '@mui/material/FormGroup';
import Checkbox from '@mui/material/Checkbox';
import IconButton from '@mui/material/IconButton';
import ButtonGroup from '@mui/material/ButtonGroup';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import PublishedWithChangesOutlined from '@mui/icons-material/PublishedWithChangesOutlined';

import RichTextEditor from '../components/RichTextEditor'
import RechargePlans from '../components/RechargePlans'
import MachineTranslationDialog, { ICON_KEY_CONTEXTS, excerptOf, NO_KEY_CONTEXTS } from './MachineTranslationDialog';
import CollapsableFields from './CollapsableFields'
import VariantsFields from './VariantsFields';
import ItemsListSkeleton from './ItemsListSkeleton';
import { Button } from '@mui/material';
import { updateAutoTranslations } from '../modules/shop'
import { formatLargeNumber, _clampToMaxLength } from '../helper'
import API from '../api/api';
import Dialog from '@mui/material/Dialog';
import Snackbar from '@mui/material/Snackbar';
import MuiAlert from '@mui/material/Alert';

// pause after the last tick change before the remaining fields are counted again
const RECOUNT_DELAY = 1000;
import { Trans, withTranslation, useTranslation } from 'react-i18next';

// new with this branch, so no merchant has it yet: everyone sees the dialog at least once. The
// live keys of the old overwrite and HTML notices are not read
const TRANSLATE_DIALOG_DISMISSED_KEY = 'ly_not_ask_again_MachineTranslationCost';
// subfield group id -> its heading, for the dialog's field summary
const GROUP_LABEL_KEYS = {
  seofields: 'itemsList.seoFieldsGroup',
  images: 'itemsList.images',
  option_names: 'itemsList.optionNames',
  metafields: 'itemsList.metafields',
  variants: 'itemsList.variants',
  variant_metafields: 'itemsList.variantMetafields',
  customfields: 'itemsList.customFields',
};
import TranslationField from './TranslationField.js';
import { UpgradePlanHint } from './PricingPlans';

const REACT_APP_ENABLE_AUTO_TRANSLATIONS = window.loadEnv("REACT_APP_ENABLE_AUTO_TRANSLATIONS", process.env.REACT_APP_ENABLE_AUTO_TRANSLATIONS);

// mounted instances: a probe reports to all of them, the one that started it may have been remounted
const brokenImageListeners = new Set();
const notifyBrokenImageListeners = () => brokenImageListeners.forEach(update => update());


// orders the entries inside a section. ItemsList's own sort orders the sections
// the sort prop is a fresh object every render, so changes are told apart by value
const fieldSortKey = (sort) => sort ? `${sort.by}|${sort.order}|${sort.token}` : '';

function sortFieldsBy(fields, sort) {
  if(!sort) {
    return fields;
  }
  const dir = sort.order === 'desc' ? -1 : 1;
  const translated = f => (f.target !== null && f.target !== '' && f.target !== undefined) ? 1 : 0;
  const outdated = f => f.outdated === true ? 1 : 0;

  // 'outdated' has one direction: flagged rows first, both groups keep their order.
  // alphabetical goes by the source text, which is what the user reads - the label is a
  // translation key, and a flat section repeats one key across every row anyway
  if(sort.by === 'outdated') {
    return fields.slice().sort((a, b) => outdated(b) - outdated(a));
  }
  return fields.slice().sort((a, b) => sort.by === 'translated'
    ? dir * (translated(a) - translated(b))
    : dir * (sortText(a).localeCompare(sortText(b))
      || String(a.label || '').localeCompare(String(b.label || ''))));
}

// the text the user sees in the row: an image shows its file name, not the URL that starts
// with "https" for every field
function sortText(field) {
  if(field.type === 'image') {
    return fileNameOf(field.source);
  }
  return visibleText(field.source);
}

// same derivation as the image field's caption: the decoded last path segment
function fileNameOf(url) {
  const value = String(url || '').trim();
  try {
    return decodeURIComponent(new URL(value).pathname.split('/').pop());
  } catch (e) {
    return value.split('/').pop().split('?')[0];
  }
}

// what the user reads of a rich text value: without the markup, every "<p>..." field would
// sort ahead of the plain ones
function visibleText(value) {
  return String(value || '')
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;|&#160;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/\s+/g, ' ')
    .trim();
}

// the captured order, applied to whatever survived the filter. Ids the snapshot does not
// know keep their own order at the end
function applyFieldRanks(fields, ranks) {
  if(!ranks) {
    return fields;
  }
  const rankOf = f => ranks[f.id] === undefined ? Number.MAX_SAFE_INTEGER : ranks[f.id];

  return fields
    .map((field, index) => ({ field, index }))
    .sort((a, b) => (rankOf(a.field) - rankOf(b.field)) || (a.index - b.index))
    .map(entry => entry.field);
}

function filterFields(fields, filterGroup, filterQuery, parentFieldId, searchFieldLabels) {
  if(!filterGroup) var filterGroup = 'all';
  if(!filterQuery) var filterQuery = '';

  let returnedArray =  Object.values(fields).filter(field => {
    if(!field.source) {
      return true;
    } else {
      // Filter for field-groups like "outdated"
      if(filterGroup && filterGroup === 'outdated') {
        if(field.outdated && field.outdated === true) {
          return true;
        } else {
          return false;
        }
      }
      // fields whose translation has unbalanced tags or lost Liquid placeholders
      if(filterGroup && filterGroup === 'markup') {
        return markupIssues(field.source, field.target) !== null;
      }

      //
      //
      // Add new conditions here
      // ...

      // show all items
      if(filterGroup && filterGroup === 'all') {
        return true;
      }
    }
  });

  returnedArray = returnedArray.filter(field => {
    if(!field.source) {
      return true;
    } else {

      // Filter in this fields:
      const fields = [
        // Check if this field is present in window.restoreFieldsData[`${id}${parentId}-${data.id}`]
        // When it is, also add the item's value here in order that the field isn't filtered and disappears when you filter for a target text which is beeing altered.
        window.restoreFieldsData[`${parentFieldId}-${field.id}`] || null, 
        field.source,
        field.target ? field.target : null,
        searchFieldLabels ? field.label : null,
      ];
      if(fields.join().toLowerCase().indexOf(filterQuery.toLowerCase()) > -1) {
        return true;
      }            
    }
  });
  
  return returnedArray;
} 



class TranslationFields extends React.Component {
  overflowIndex = -1;
  // fields unticked in the confirmation dialog's table: left out of the run, reset on close.
  // An instance field, not state: the run reads it synchronously when confirm re-enters
  translateExcluded = new Set();
  // field id => the translation service's reason for leaving the text as it is (word count with
  // skip_details); such rows start unticked like the section's own flagged fields
  serviceSkips = {};

  // records the service's skips from a word count result; returns how many rows it newly unticked
  noteServiceSkips = (res) => {
    let added = 0;
    (res?.skip_details?.fields || []).forEach(entry => {
      if(!entry || entry.key === undefined) return;
      this.serviceSkips[String(entry.key)] = entry.reason || '';
      if(!this.translateExcluded.has(String(entry.key))) {
        this.translateExcluded.add(String(entry.key));
        added++;
      }
    });
    return added;
  }
  recountToken = 0;
  // the run being counted or confirmed: a selection {empty, outdated, filled} (see modeSelection)
  pendingMode = 'empty';


  constructor(props) {
    super(props);


    const cache = new CellMeasurerCache({
      defaultHeight: 100,
      fixedWidth: true,
      //keyMapper: () => 1,
    });
    this.cache = cache;
    
    this.state = {
      data: props.data,
      loadingTranslations: false,
      countingWords: false,
      loadingTranslationsError: false,
      canAutoTranslate: true,
      //forceUpdate: props.forceUpdate ? props.forceUpdate : false,
      forceUpdate: props.forceUpdate,
      filterQuery: '',
      showRechargePlans: false,
      // the one dialog between the word count and the paid request, see renderTranslateDialog
      translateDialog: null,
      // a recount is out after a tick change in the dialog's table
      translateRecounting: false,
      // row under the mouse in the dialog's field table (the grid has no row elements to style)
      translateHoverRow: null,
      translateDialogDismissed: !!localStorage.getItem(TRANSLATE_DIALOG_DISMISSED_KEY),
      translateDialogFieldsOpen: false,
      autoTranslateMenuAnchor: null,
      // the dropdown's checklist: which kinds of fields the next run takes
      translateSelection: { empty: true, outdated: false, filled: false },
      scrollToIndex: props.scrollToIndex ? props.scrollToIndex : -1,
      // the image row whose dialog should open next (the arrows in the image dialog), and the
      // dialog view to restore there: expanded picker option, original comparison open
      openImageFieldId: null,
      openImageView: null,
      // ids of image fields whose target URL no longer loads
      brokenTargets: [],
    }
  }

  componentDidMount = () => {

    this.setState({
      filterQuery: this.props.filterQuery ? this.props.filterQuery : '',
    });

    brokenImageListeners.add(this.updateBrokenTargets);
    // opening an item probes again: a file deleted since the last probe still reads as loading
    this.checkBrokenImageTargets(this.props.data, true);

    this.resizeList();  
    
    setTimeout(() => {
      if(this.windowScrollerRef) {
        this.windowScrollerRef.updatePosition();   
      }
    }, 1000);
  }

  // one-shot reorder: a live sort would slide the field out from under the cursor as
  // soon as typing makes it count as translated
  syncFieldRanks = (fields) => {
    const sort = this.props.fieldSort;
    if(!sort) {
      this.fieldRanks = null;
      this.fieldRanksFor = null;
      return;
    }

    const first = fields.length > 0 ? fields[0].id : null;
    const last = fields.length > 0 ? fields[fields.length - 1].id : null;
    const signature = `${sort.token}|${fields.length}|${first}|${last}`;
    if(this.fieldRanksFor === signature) {
      return;
    }

    this.fieldRanks = {};
    sortFieldsBy(fields, sort).forEach((field, index) => { this.fieldRanks[field.id] = index; });
    this.fieldRanksFor = signature;
  }

  componentWillUnmount() {
    this.unmounted = true;
    brokenImageListeners.delete(this.updateBrokenTargets);
    clearTimeout(this.pendingImageScrollTimer);
  }

  componentWillReceiveProps = (newProps) => {
    this.checkBrokenImageTargets(newProps.data);
    this.setState({
      data: newProps.data,
      //forceUpdate: newProps.forceUpdate ? newProps.forceUpdate : this.state.forceUpdate,
      //forceUpdate: newProps.forceUpdate,
      filterQuery: newProps.filterQuery ? newProps.filterQuery : '',
    })

    if(this.props.triggerClearAllTranslations !== newProps.triggerClearAllTranslations) {
      this.handleClearAllTranslations();
    }

    if(this.props.forceUpdate !== newProps.forceUpdate) {
      this.setState({
        //forceUpdate: newProps.forceUpdate ? newProps.forceUpdate : this.state.forceUpdate,
        forceUpdate: newProps.forceUpdate,
      })
    }

    if(this.props.scrollToIndex !== newProps.scrollToIndex) {
      this.setState({scrollToIndex: newProps.scrollToIndex})
    }

    // the list's scrollToIndex does not scroll (see scrollRowIntoView), a field jump goes the
    // way the image dialog's does, settling until the row is in view
    const request = newProps.scrollToFieldRequest;
    if(request && request !== this.props.scrollToFieldRequest && (newProps.data || []).some(f => f.id === request.id)) {
      this.pendingImageScrolls = 0;
      this.pendingScrollFieldId = request.id;
      this.settlePendingImageScroll();
    }

    const currentLanguage = this.props.shop.shop.edit_language;
    const newLanguage = newProps.shop.shop.edit_language;
    if(currentLanguage != newLanguage) {
      this.resizeList(); 
    }
  }

  // Re-measure the rows only when something that sizes them changed. Clearing on every update
  // threw every measured height away on each scroll event (the scroll handler updates state),
  // so rows above the viewport fell back to the estimate and the content jumped while
  // scrolling up
  componentDidUpdate(prevProps, prevState) {
    if(
      prevProps.data !== this.props.data ||
      prevProps.forceUpdate !== this.props.forceUpdate ||
      prevProps.largeImages !== this.props.largeImages ||
      prevProps.filterGroup !== this.props.filterGroup ||
      prevState.filterQuery !== this.state.filterQuery ||
      prevState.filterGroup !== this.state.filterGroup ||
      // the height cache is keyed by row index: a re-sort moves other rows under those indexes
      fieldSortKey(prevProps.fieldSort) !== fieldSortKey(this.props.fieldSort)
    ) {
      this.resizeList();
    }
  }

  // probes every image target once (cached module-wide) and collects the field ids
  // whose translation URL no longer loads - drives the broken-images warning banner
  checkBrokenImageTargets = (fields, recheck = false) => {
    (fields || []).forEach(field => {
      if(field.type !== 'image' || !field.target || field.target === '' || isShopImageRef(field.target)) return;
      const url = field.target;
      const cached = imageProbeResult(url);
      if(cached === true || cached === null || (cached === false && !recheck)) return;
      probeImage(url, notifyBrokenImageListeners, recheck);
    });
    this.updateBrokenTargets(fields);
  }

  updateBrokenTargets = (fields) => {
    if(this.unmounted) return;
    const usedFields = fields || this.state.data || [];
    const broken = usedFields
      .filter(f => f.type === 'image' && f.target && imageProbeResult(f.target) === true)
      .map(f => f.id);
    if(broken.join('|') !== this.state.brokenTargets.join('|')) {
      this.setState({ brokenTargets: broken });
    }
  }

  handleClearBrokenTranslations = () => {
    let newData = this.state.data.slice();
    newData.forEach((field, i) => {
      if(!this.state.brokenTargets.includes(field.id)) return;
      newData[i].initial = this.state.data[i].target;
      newData[i].target = '';
      this.props.onChange(null, newData[i]);
    });
    this.setState({
      data: newData,
      brokenTargets: [],
      forceUpdate: true
    }, () => {
      this.setState({ forceUpdate: false });
    });
  }

  handleClearAllTranslations = () => {
    let newData = this.state.data.slice();
    newData.forEach((item, i, a) => {
      // SEO subfields render in a plain component that the virtualized list may have
      // unmounted, so no nested instance is around to react to the trigger
      if(item.type === 'subfields' && item.id === 'seofields') {
        item.fields.forEach(subfield => {
          if(subfield.target === '') return;
          this.props.onChange(null, {
            id: subfield.id,
            changed: true,
            initial: subfield.target,
            target: '',
            parentField: item.id,
          });
          subfield.initial = subfield.target;
          subfield.target = '';
        });
        return;
      }

      newData[i].initial = this.state.data[i].target;
      newData[i].target = '';

      this.props.onChange(null, newData[i]);
    });
    this.setState({
      data: newData,
      forceUpdate: true
    }, () => {
      this.setState({
        forceUpdate: false
      });      
    });
  }

  openRechargePlans = () => {
    this.setState({ showRechargePlans: true, translateDialog: null });
  }

  // Confirm and "translate what fits" both re-enter with the overflow index set, which skips
  // the count block, so the dialog does not come back
  confirmTranslateDialog = () => {
    this.setState({ translateDialog: null }, () => {
      this.bulkTranslateSection(this.overflowIndex, this.pendingMode);
    });
  }

  // cancel means "keep asking", like the old dialog did
  // "nothing to translate" offers the other kinds of run right in the dialog: same entry point
  // as the dropdown's checklist, the dialog just closes first so the count does not run behind it
  runAlternativeFromDialog = (selection) => {
    clearTimeout(this.recountTimer);
    this.translateExcluded = new Set();
    this.serviceSkips = {};
    // the dialog stays open with its spinner layer while the new count runs: closing it first
    // left the screen without a dialog for the whole round trip
    this.setState({ translateRecounting: true }, () => this.runAutoTranslation(selection));
  }

  closeTranslateDialog = () => {
    localStorage.removeItem(TRANSLATE_DIALOG_DISMISSED_KEY);
    clearTimeout(this.recountTimer);
    this.translateExcluded = new Set();
    this.serviceSkips = {};
    this.setState({ translateDialog: null, translateDialogDismissed: false, translateRecounting: false });
  }

  // a tick change in the table: the count route only ever returns one total, so the remaining
  // set is counted again (free - no words are spent) once the clicking has paused for a
  // while - unticking a handful of fields at reading speed is one request, not one per click
  // the set holds string ids throughout: field ids are numbers in some sections, the service's
  // skip keys and the map's keys are strings, and a mixed set missed its own rows
  toggleTranslateField = (id) => {
    if(this.translateExcluded.has(String(id))) this.translateExcluded.delete(String(id));
    else this.translateExcluded.add(String(id));
    clearTimeout(this.recountTimer);
    this.setState({ translateRecounting: true });
    this.recountTimer = setTimeout(this.recountTranslateDialog, RECOUNT_DELAY);
  }

  recountTranslateDialog = async () => {
    const d = this.state.translateDialog;
    if(!d) return;
    // a reply from an earlier count that lands after a later one must not win
    const recountToken = ++this.recountToken;
    let strings = this.runStrings(this.pendingMode);
    const keys = Object.keys(strings);
    const included = new Set(keys.map(String));
    const overwriteNum = d.fieldSummary.filter(line => line.replaces && included.has(String(line.id))).length;
    if(keys.length === 0) {
      this.overflowIndex = -1;
      this.setState({
        translateRecounting: false,
        translateDialog: { ...d, fieldsNum: 0, wordsNum: 0, missingNum: 0, partFieldsNum: 0, overwriteNum: 0, hasMuchHtml: false },
      });
      return;
    }
    const res = await new Promise(resolve => API.autoTranslateWordCount(this.props.from, this.props.to, strings, resolve));
    if(!this.state.translateDialog || recountToken !== this.recountToken) return;
    this.countWords(strings, true); // sets this.overflowIndex for "translate what fits"
    (res?.skip_details?.fields || []).forEach(entry => { if(entry?.key !== undefined) this.serviceSkips[String(entry.key)] = entry.reason || ''; });
    if(!res || res.success === false) {
      // keep the previous numbers rather than showing a count that is not for this set
      this.setState({ translateRecounting: false, loadingTranslationsError: true, showMessage: res && res.show_message ? res.show_message : false });
      return;
    }
    const wordsNum = res.word_count ?? 0;
    const availableWords = this.props.shop.shop.auto_translations.available_words;
    const outQuota = wordsNum > availableWords;
    this.setState({
      translateRecounting: false,
      translateDialog: {
        ...d,
        fieldsNum: keys.length,
        wordsNum,
        availableWords,
        missingNum: outQuota ? wordsNum - availableWords : 0,
        partFieldsNum: outQuota ? this.overflowIndex + 1 : 0,
        overwriteNum,
        hasMuchHtml: this.checkHtmlAmount(strings),
      },
    });
  }

  // one switch, two places: the dialog's "don't show again" and the menu's "show details"
  setTranslateDialogDismissed = (dismissed) => {
    if(dismissed) localStorage.setItem(TRANSLATE_DIALOG_DISMISSED_KEY, true);
    else localStorage.removeItem(TRANSLATE_DIALOG_DISMISSED_KEY);
    this.setState({ translateDialogDismissed: dismissed });
  }

  toggleTranslateDialogDismissed = (event) => {
    this.setTranslateDialogDismissed(event.target.checked);
  }

  // The slice of a paged list that is on screen. Sections without paging pass through.
  pageSlice = (fields) => {
    const { fieldsPerPage, fieldPage } = this.props;
    if(!fieldsPerPage) return fields;
    return fields.slice(fieldPage * fieldsPerPage, (fieldPage + 1) * fieldsPerPage);
  }

  // Collects the source strings of all visible/filtered fields that are eligible
  // for machine translation. `mode` decides which target states qualify:
  // 'empty'  → only untranslated fields (default bulk translation)
  // 'all'    → untranslated and already translated fields (overwrite mode)
  // 'filled' → only already translated fields (used to count what an overwrite affects)
  // Image subfields hold image URLs in source/target, so they must never be sent to the
  // machine translation service. `mode` picks the targets: empty, filled or all
  // the shared rule set (machineTranslationRules.js) decides; the section's flag is handled by
  // the callers as a default the dialog may override
  _wantsTranslation = (field, mode) => wantsMachineTranslation(field, mode)

  modeSelection = (mode) => selectionOf(mode)

  // how many existing translations a run with this selection replaces
  countReplaced = (mode) => {
    let n = 0;
    this._forEachVisibleField(field => {
      if(isSkippedByDefault(field)) return;
      if(field.target !== '' && this._wantsTranslation(field, mode)) n++;
    });
    return n;
  }

  toggleTranslateSelection = (key) => {
    this.setState({ translateSelection: { ...this.state.translateSelection, [key]: !this.state.translateSelection[key] } });
  }

  // Only machine-translate what the list is showing right now: the filter narrows the
  // fields, the page slice narrows them again. Ranks are applied before the slice so it
  // picks the same entries as the rendered page.
  _forEachVisibleField = (callback) => {
    const visibleFields = this.pageSlice(applyFieldRanks(
      filterFields(this.state.data, this.props.filterGroup, this.state.filterQuery, this.props.itemId, this.props.searchFieldLabels),
      this.fieldRanks
    ));
    visibleFields.forEach(field => {
      if(field.type === 'subfields') {
        // the images group is no longer skipped wholesale: its image entries are excluded by
        // _wantsTranslation, but the alt text fields riding along with them are real MT candidates
        const visibleSubfields = filterFields(field.fields, this.props.filterGroup, this.state.filterQuery, field.id, this.props.searchFieldLabels);
        visibleSubfields.forEach(subfield => callback(subfield, field));
      } else {
        callback(field, null);
      }
    });
  }

  // what a run covers, for the dialog's expandable list: top-level fields by label, subfield
  // groups (variants, metafields, ...) as a count - their values would say nothing
  // the dialog's field table: one row per field with its key and the start of source and target;
  // `replaces` marks rows whose translation the run would overwrite (only an overwrite run has any)
  _summaryOf = (ids) => {
    const { t } = this.props;
    const wanted = new Set(ids.map(String));
    const excerpt = excerptOf;
    // grouped fields are rows of their own with the plainest key that still says where they
    // belong: "SEO title" / "SEO description", otherwise just the group ("Variants", "Custom Fields")
    const groupKey = (group, field) => {
      if(group.id === 'seofields') {
        if(/title/i.test(field.id)) return t('itemsList.summaryKeySeoTitle');
        if(/desc/i.test(field.id)) return t('itemsList.summaryKeySeoDescription');
      }
      // of the images group only the alt texts reach a run (the images are URLs)
      if(group.id === 'images') return t('itemsList.summaryKeyImageAlt');
      return GROUP_LABEL_KEYS[group.id] ? t(GROUP_LABEL_KEYS[group.id]) : (group.label || group.id);
    };
    const lines = [];
    this._forEachVisibleField((field, group) => {
      // Shopify references are no candidates, but a row of the item the run leaves out is listed
      // all the same, greyed with its reason - otherwise three fields show and one is silently ignored
      const reference = referenceReasonOf(field);
      if(!wanted.has(String(field.id)) && !(reference && matchesSelection(field, this.pendingMode))) return;
      const filled = field.target !== '' && field.target !== null && field.target !== undefined;
      const serviceReason = this.serviceSkips[String(field.id)];
      lines.push({
        id: field.id, fullKey: group ? groupKey(group, field) : (field.label || field.id), source: excerpt(field.source), target: excerpt(field.target), replaces: filled && !reference,
        suggestedSkip: isSkippedByDefault(field) || serviceReason !== undefined || reference !== null,
        // the section's own reason first (numbers, sizes), the service's where only it objects
        skipReason: reference || skipReasonOf(field) || (serviceReason !== undefined ? 'service' : null),
        skipHint: serviceReason ?? null,
      });
    });
    // the dialog sorts: replaced, then untranslated, then the flagged rows
    return lines;
  }

  collectTranslatableStrings = (mode = 'empty') => {
    let stringsArr = {};
    this._forEachVisibleField(field => {
      // left out by default: the section's flag (numbers, sizes), handles, product types
      if(isSkippedByDefault(field)) return;
      if(this._wantsTranslation(field, mode)) stringsArr[field.id] = field.source.toString();
    });
    return stringsArr;
  }

  // the dialog's candidates: like collectTranslatableStrings, but the section's flag is a default
  // rather than a verdict - flagged fields are listed too, unticked, and the merchant may tick them
  collectCandidateStrings = (mode = 'empty') => {
    let stringsArr = {};
    this._forEachVisibleField(field => {
      if(this._wantsTranslation(field, mode)) stringsArr[field.id] = field.source.toString();
    });
    return stringsArr;
  }

  // ids of the flagged candidates - the default exclusions of a fresh run
  suggestedSkipIds = (mode = 'empty') => {
    const ids = new Set();
    this._forEachVisibleField(field => {
      if(isSkippedByDefault(field) && this._wantsTranslation(field, mode)) ids.add(String(field.id));
    });
    return ids;
  }

  // what the run sends: the candidates minus the dialog's unticked rows
  runStrings = (mode) => {
    let strings = this.collectCandidateStrings(mode);
    this.translateExcluded.forEach(id => delete strings[id]);
    return strings;
  }

  bulkTranslateSection = async (maxIndex, mode = 'empty') => {
    this.overflowIndex = -1;
    this.pendingMode = mode;
    // a fresh run starts with the flagged fields (numbers, sizes) unticked and everything else
    // ticked; confirm re-enters with the dialog's ticks
    if(maxIndex === null) {
      this.translateExcluded = this.suggestedSkipIds(mode);
      this.serviceSkips = {};
    }
    // the source strings of the visible/filtered fields the run sends
    let stringsArr = this.runStrings(mode);
    // The button stays enabled even where the page holds nothing to write to, so bail out
    // before the word count asks the API about an empty set
    if(Object.keys(stringsArr).length === 0) {
      const candidates = Object.keys(this.collectCandidateStrings(mode));
      if(candidates.length === 0 || maxIndex !== null) {
        // a re-entry with nothing left (every candidate skipped): never leave the bar spinning
        this.reportBulkTranslationRun(false);
        this.setState({ loadingTranslations: false });
        return null;
      }
      // only flagged fields (numbers, sizes): no words to count yet, but the dialog opens so the
      // merchant can tick them in - the recount does the rest
      this.setState({
        translateDialogDismissed: !!localStorage.getItem(TRANSLATE_DIALOG_DISMISSED_KEY),
        translateDialogFieldsOpen: true,
        translateRecounting: false,
        translateDialog: {
          fieldsNum: 0,
          fieldSummary: this._summaryOf(candidates),
          wordsNum: 0,
          availableWords: this.props.shop.shop.auto_translations.available_words,
          missingNum: 0,
          partFieldsNum: 0,
          overwriteNum: 0,
          hasMuchHtml: false,
        },
      });
      return null;
    }

    // Up before the word count, not after it: that request is a round trip of its own, and
    // until now the bar sat there looking idle for the whole of it
    this.setState({
      loadingTranslations: true,
    })
    this.reportBulkTranslationRun('counting');
    // Captured before the count: a switch during it bumps the token, and the run has to stop
    // before the paid request goes out
    const runToken = API.runToken;

    // Handle events that block the process
    this.countWords(stringsArr, true); // sets this.overflowIndex as side effect
    if(maxIndex === null) {
      this.setState({countingWords: true});
      const wordCountRes = await new Promise(resolve => API.autoTranslateWordCount(this.props.from, this.props.to, stringsArr, resolve));
      this.setState({countingWords: false});
      // The merchant switched while the count was out - no words spent yet, so just stop
      if(API.runToken !== runToken) {
        this.reportBulkTranslationRun(false);
        this.setState({ loadingTranslations: false });
        return null;
      }
      // A failed count read as zero words would pass the quota check and send
      // the paid request on the strength of an error.
      if(!wordCountRes || wordCountRes.success === false) {
        this.reportBulkTranslationRun(false);
        this.setState({
          loadingTranslations: false,
          loadingTranslationsError: true,
          showMessage: wordCountRes && wordCountRes.show_message ? wordCountRes.show_message : false,
        });
        return null;
      }
      const wordsNum = wordCountRes.word_count ?? 0;
      const availableWords = this.props.shop.shop.auto_translations.available_words;
      const overwriteNum = this.countReplaced(mode);
      const outQuota = wordsNum > availableWords;
      this.reportBulkTranslationRun(false);
      // the service's skips are unticked here; word_count never included them, so no recount
      const serviceSkipped = this.noteServiceSkips(wordCountRes);
      const remainingStrings = this.runStrings(mode);
      const remaining = Object.keys(remainingStrings).length;
      if(serviceSkipped > 0) this.countWords(remainingStrings, true); // overflow index over the rows that run
      // The cost is known now, nothing is spent yet: this is the one place to ask. A plain run
      // (nothing overwritten, enough words) can be waved through for good; an overwrite or a
      // short quota always asks, and so does a run with nothing left to send - the merchant must
      // learn why nothing happens. Confirm re-enters with the overflow index set, which skips this block
      if(!outQuota && overwriteNum === 0 && remaining > 0 && localStorage.getItem(TRANSLATE_DIALOG_DISMISSED_KEY)) {
        this.bulkTranslateSection(this.overflowIndex, mode);
        return null;
      }
      this.setState({
        loadingTranslations: false,
        translateDialogDismissed: !!localStorage.getItem(TRANSLATE_DIALOG_DISMISSED_KEY),
        translateDialogFieldsOpen: false,
        translateRecounting: false,
        translateDialog: {
          fieldsNum: remaining,
          fieldSummary: this._summaryOf(Object.keys(this.collectCandidateStrings(mode))),
          wordsNum,
          availableWords,
          missingNum: outQuota ? wordsNum - availableWords : 0,
          // the leading fields that still fit the quota (the overflow index is the last one)
          partFieldsNum: outQuota ? this.overflowIndex + 1 : 0,
          overwriteNum,
          hasMuchHtml: this.checkHtmlAmount(stringsArr),
        },
      });
      return null;
    }

    // Handle overflow
    let newStringsArr = {};
    Object.keys(stringsArr).map((key, currIndex) => {
      if(currIndex <= this.overflowIndex) {
        newStringsArr[key] = stringsArr[key];
      }
    });
    stringsArr = newStringsArr;
    if(Object.keys(stringsArr).length === 0) {
      this.reportBulkTranslationRun(false);
      this.setState({ loadingTranslations: false });
      return null;
    }

    // The phase matters upstairs: the words are spent while this request is out, so only
    // 'translating' is worth a warning before leaving; 'counting' just keeps the overlay up.
    this.reportBulkTranslationRun('translating');
    // Captured here, not read in the callback: `to` is an iso code and the save needs the id,
    // and by the time the response lands the merchant may have selected another language
    const runLanguage = this.props.toLanguageId;
    API.autoTranslateStrings(this.props.from, this.props.to, stringsArr, (res) => {
      // Switching the selector or leaving the section cancels the run. The request itself could
      // not be stopped, but nothing of it is written: the merchant was told the translation is
      // lost, so writing it anyway - to whichever language - is the wrong answer.
      if(API.runToken !== runToken) {
        // the words were spent all the same, and the balance is shop-wide, not per language -
        // without this the bar shows the old count until a reload
        if(res && res.auto_translations) {
          this.props.updateAutoTranslations(
            res.auto_translations.total_used_words,
            res.auto_translations.available_words,
          )
        }
        this.reportBulkTranslationRun(false);
        this.setState({loadingTranslations: false});
        return null;
      }

      if(res.success === false || !res.auto_translations ) {
        this.reportBulkTranslationRun(false);
        this.setState({ 
          loadingTranslations: false, 
          loadingTranslationsError: true,
          showMessage: res.show_message ? res.show_message : false
        });
        return null;
      }

      this.props.updateAutoTranslations(
        res.auto_translations.total_used_words,
        res.auto_translations.available_words,
      )

      // Update res of all section target fields
      let newData = this.state.data.slice();

      // Every translated field is reported, not just the mounted ones: a windowed or paged
      // list leaves the rest unmounted, so the forceUpdate pulse below cannot mark them and
      // the run would only ever be saved for what was on screen
      Object.keys(stringsArr).forEach((key, i) => {
        // a string the service left out of its answer (skipped on its side) stays as it was: writing
        // the missing value made the field undefined, and the save then posted the id without a text
        if(res.Strings[key] === undefined) return;
        const field = newData.find(x => x.id == key);
        if(field) {
          field.initial = field.target;
          field.target = _clampToMaxLength(res.Strings[key], field.maxLength);
          this.props.onChange(null, field);
        } else {
          if(newData.find(x => x.type == 'subfields')) {
            newData.filter(x => x.type == 'subfields').forEach((item, p) => {
              const subfield = item.fields.find(x => x.id == key);
              if(subfield) {
                subfield.initial = subfield.target;
                subfield.target = _clampToMaxLength(res.Strings[key], subfield.maxLength);
                subfield.parentField = item.id;
                this.props.onChange(null, subfield);
              }
            });
          }
        }
      });

      this.setState({
        data: newData,
        forceUpdate: true,
        loadingTranslations: false,
        loadingTranslationsError: false,
        showMessage: false
      }, () => {
        this.setState({forceUpdate: false });
      });
      setTimeout(() => {
        // the run's overlay stays up through the save (the list ends the phase when the save is
        // through): the section's own save overlay fades in late, and switching over left a gap.
        // The translated fields ride along: a result identical to the old translation still
        // counts as a fresh translation (the size chart clears its outdated flag on that)
        this.reportBulkTranslationRun('saving');
        this.props.onSave(null, runLanguage, Object.keys(stringsArr).filter(key => res.Strings[key] !== undefined));
      }, 100);
    });
  }

  // phase: 'counting' | 'translating' | false
  reportBulkTranslationRun = (phase) => {
    if(typeof this.props.onBulkTranslationRunChange === 'function') {
      this.props.onBulkTranslationRunChange(phase);
    }
  }

  countWords = (arr, calcOverflow) => {
    let deleteEmptySliceValues = function(inputArray) {
      var r = [];
      inputArray.forEach(function(item) {
        if (item !== "") {
          r.push(item);
        }
      });
      return r
    }

    let sum = Object.values(arr).reduce((pv, cv, ci) => {
      let singleSourceString = cv.toString();
      let charCount = singleSourceString.length;
      let calc_words_by_chars = charCount / 6;
      singleSourceString = singleSourceString.replace(/-/g, " ");
      singleSourceString = singleSourceString.replace(/</g, " ");
      singleSourceString = singleSourceString.replace(/>/g, " ");
      singleSourceString = singleSourceString.replace(/\"/g, " ");
      singleSourceString = singleSourceString.replace(/\n/g, " ");
      let wordSlice = singleSourceString.split(" ");
      wordSlice = deleteEmptySliceValues(wordSlice);

      let diff = calc_words_by_chars - wordSlice.length;
      if (diff < 0) diff = diff * -1;
      let diffPercent = (diff * 100) / wordSlice.length
      let price_words = wordSlice.length;
      if (diffPercent > 10 && calc_words_by_chars > wordSlice.length) {
          price_words = calc_words_by_chars;
      }
      let totalWords = pv + parseInt(price_words);

      if(calcOverflow && totalWords <= this.props.shop.shop.auto_translations.available_words) {
        this.overflowIndex = ci;
      }
      return totalWords;
    }, 0);
    return sum;
  }

  checkHtmlAmount = (arr) => {
    let htmlCount = 0;
    Object.entries(arr).map((item) => {
      if(item[0] === 'body_html' || item[0] === 'summary_html') {
        let plainTxt = item[1].replace(/<[^>]*>?/gm, '');
        let allWords = this.countWords({[item[0]]: item[1]});
        let txtWords = this.countWords({txt: plainTxt});
        let count = allWords - txtWords;
        if(count > htmlCount) htmlCount = count;
      }
    });
    return htmlCount > 250 ? true : false;
  }
 
  // True as soon as at least one field of this section can be machine-translated,
  // either because it is still empty or because its existing translation can be
  // overwritten via the "overwrite" option of the machine translation bar.
  isAutoTranslationAvailable = () => {
    return (this.hasEmptyTranslatableFields() || this.countOverwritableTranslations() > 0);
  }

  countOverwritableTranslations = () => {
    if(this.state.data.length === 0) return 0;
    return Object.keys(this.collectTranslatableStrings('filled')).length;
  }

  countEmptyTranslatableFields = () => {
    if(this.state.data.length === 0) return 0;
    return Object.keys(this.collectTranslatableStrings('empty')).length;
  }

  // translated fields whose original changed since (sections that carry the outdated flag)
  countOutdatedTranslations = () => {
    if(this.props.flagsOutdated !== true) return 0;
    if(this.state.data.length === 0) return 0;
    return Object.keys(this.collectTranslatableStrings('outdated')).length - this.countEmptyTranslatableFields();
  }

  // Number of fields an overwrite run would translate: the empty ones plus the
  // ones that already have a translation.
  countAllTranslatableFields = () => {
    if(this.state.data.length === 0) return 0;
    return Object.keys(this.collectTranslatableStrings('all')).length;
  }

  hasEmptyTranslatableFields = () => {
    let emptyFields = false;
    if(this.state.data.find(x => (x.source !== '' && x.target === '' && !x.isHidden))) {
      emptyFields = true;
    }
    if(!emptyFields && this.state.data.find(x => x.type == 'subfields')) {
      this.state.data.filter(x => x.type == 'subfields').forEach(item => {
        // image entries never count as machine-translatable, but their alt text fields do
        if(!emptyFields && item.fields.find(x => (x.type !== 'image' && x.source !== '' && x.target === '' && !x.isHidden))) {
          emptyFields = true;
        }
      });
    }
    return (this.state.data.length > 0 && emptyFields);
  }

  openAutoTranslateMenu = (event) => {
    this.setState({autoTranslateMenuAnchor: event.currentTarget});
  }

  closeAutoTranslateMenu = () => {
    this.setState({autoTranslateMenuAnchor: null});
  }

  // The dialog after the count is the confirmation, and a run that replaces translations always
  // gets it. Main button and dropdown run the same checklist - the button used to switch to a
  // full overwrite on its own once nothing was left to fill, which the checklist never showed.
  // A selection that yields nothing opens the dialog's "nothing to translate" instead of a silent
  // click; the merchant ticks "existing translations" to retranslate
  runAutoTranslation = (mode) => {
    this.setState({ autoTranslateMenuAnchor: null });
    if(Object.keys(this.collectTranslatableStrings(mode)).length === 0) {
      this.pendingMode = mode;
      // the default skips (handle, product type) start unticked here as in a normal run; a tick
      // brings them in and the recount takes over
      this.translateExcluded = this.suggestedSkipIds(mode);
      this.serviceSkips = {};
      this.setState({
        translateDialogDismissed: false,
        translateDialogFieldsOpen: false,
        // an alternative run started from the open dialog leaves the spinner layer up until here
        translateRecounting: false,
        translateDialog: {
          fieldsNum: 0,
          fieldSummary: this._summaryOf(Object.keys(this.collectCandidateStrings(mode))),
          wordsNum: 0,
          availableWords: this.props.shop.shop.auto_translations.available_words,
          missingNum: 0,
          partFieldsNum: 0,
          overwriteNum: 0,
          hasMuchHtml: false,
        },
      });
      return;
    }
    this.bulkTranslateSection(null, mode);
  }

  onCloseRechargePlans = () => {
    this.setState({
      showRechargePlans: false
    })
  }

  onBuyWords = (plan) => {
    API.buyWords(
      plan,
      res => {
        //console.log(res)
      }
    )
  }

  resizeList = () => {
    this.cache.clearAll();
    if (this.listRef) {
      this.listRef.forceUpdateGrid();
    }
  }

  calcRowHeight = (rows) => {
    if(rows === 5) return (window.innerWidth < 960) ? 213 : 143;
    return (window.innerWidth < 960) ? 213 : 119;
  }

  // the rows the virtualized list currently has mounted, overscan included
  onRowsRendered = ({ overscanStartIndex, overscanStopIndex }) => {
    this.mountedRowRange = [overscanStartIndex, overscanStopIndex];
    this.settlePendingImageScroll();
  }

  scrollElement = () => document.querySelector(this.props.scrollElement ? this.props.scrollElement : '#mainFrame');

  // the DOM row of a field once it is mounted - TranslationField stamps data-field-id
  fieldRowEl = (fieldId) => {
    const root = this._virtualListEl || this._staticListEl;
    return root ? root.querySelector(`[data-field-id="${CSS.escape(String(fieldId))}"]`) : null;
  }

  // fully inside the frame, below the sticky item header
  isRowInView = (rowEl, scrollElement) => {
    const row = rowEl.getBoundingClientRect();
    const frame = scrollElement.getBoundingClientRect();
    return row.top >= frame.top + 130 && row.bottom <= frame.bottom;
  }

  // Centers a field's row in the frame. A mounted row is placed by its real position; an
  // unmounted one by the list's layout, which rests on estimates for rows never rendered and
  // lands short - the settle loop then corrects against the real row once it exists.
  // Returns true when the row is already where it should be
  scrollFieldRowIntoView = (fieldId) => {
    const scrollElement = this.scrollElement();
    if(!scrollElement) return true;
    const rowEl = this.fieldRowEl(fieldId);
    if(rowEl) {
      if(this.isRowInView(rowEl, scrollElement)) return true;
      const row = rowEl.getBoundingClientRect();
      const frame = scrollElement.getBoundingClientRect();
      scrollElement.scrollTo({ top: Math.max(0, scrollElement.scrollTop + row.top - frame.top - (scrollElement.clientHeight - row.height) / 2), behavior: 'auto' });
      return false;
    }
    const rowIndex = this.visibleData.findIndex(field => field.id === fieldId);
    if(rowIndex >= 0) this.scrollRowIntoView(rowIndex);
    return false;
  }

  // re-aims after the list rendered (or after a moment, for the static list), a few rounds at
  // most - then the open request is dropped rather than left to pop the dialog whenever the
  // merchant happens to scroll past that row
  settlePendingImageScroll = () => {
    if(this.pendingScrollFieldId == null) return;
    clearTimeout(this.pendingImageScrollTimer);
    this.pendingImageScrollTimer = setTimeout(() => {
      if(this.unmounted || this.pendingScrollFieldId == null) return;
      if(this.pendingImageScrolls >= 6) {
        this.pendingScrollFieldId = null;
        if(this.state.openImageFieldId !== null) this.setState({ openImageFieldId: null });
        return;
      }
      this.pendingImageScrolls += 1;
      if(this.scrollFieldRowIntoView(this.pendingScrollFieldId)) {
        this.pendingScrollFieldId = null;
        return;
      }
      this.settlePendingImageScroll();
    }, 250);
  }

  // The list's own scrollToIndex is dead under the WindowScroller (react-virtualized 9.22: the
  // scroll position it computes is overwritten by the scroller's scrollTop prop in the same
  // update and never reaches the scroll element). Its row offset is still right, so the scroll
  // element is moved here directly, like the item jump in ItemsList does
  scrollRowIntoView = (rowIndex) => {
    const list = this.listRef;
    const scrollElement = document.querySelector(this.props.scrollElement ? this.props.scrollElement : '#mainFrame');
    if(!list || !this._virtualListEl || !scrollElement) return;
    // getOffsetForRow clamps to the list's total height, in which rows beyond the last visited
    // one count 30px each: a long jump would land far short and walk up in chunks. The row's
    // own position is not clamped
    const manager = list.Grid?.state?.instanceProps?.rowSizeAndPositionManager;
    const cell = manager ? manager.getSizeAndPositionOfCell(rowIndex) : null;
    const offset = cell
      ? cell.offset - Math.max(0, (scrollElement.clientHeight - cell.size) / 2)
      : list.getOffsetForRow({ alignment: 'center', index: rowIndex });
    // the container is still sized by the old estimate and would cap the scroll: the list
    // re-renders with the positions just computed, the frame scrolls once it has that height
    list.forceUpdateGrid();
    requestAnimationFrame(() => {
      if(this.unmounted || !this._virtualListEl) return;
      const listTop = this._virtualListEl.getBoundingClientRect().top - scrollElement.getBoundingClientRect().top + scrollElement.scrollTop;
      scrollElement.scrollTo({ top: Math.max(0, listTop + offset), behavior: 'auto' });
    });
  }

  onFilterChange = (event) => {
    this.setState({ filterQuery: event.target.value })
  }

  onResetQuery = () => {
    this.setState({ 
      filterQuery: '' 
    });
  }



  clearScrollToIndex = () => {
    if(this.state.scrollToIndex !== -1) this.setState({ scrollToIndex: -1 });
  }

  // the image dialog's arrows step through the image rows of this item only. Every row owns
  // its dialog, so the current one closes and the neighbor is told to open. In the virtualized
  // list an unmounted neighbor is scrolled in and opens on mount - but only then: scrolling
  // swaps every row for a placeholder, which would unmount a neighbor that just opened
  // computed once per field list: every row asks, and the list can hold thousands of rows
  imageFieldIds = () => {
    if(this._imageFieldIdsOf !== this.visibleData) {
      this._imageFieldIdsOf = this.visibleData;
      this._imageFieldIds = this.visibleData
        .filter(field => field.type === 'image' || (field.type === 'text' && isImageSource(field.source)))
        .map(field => field.id);
    }
    return this._imageFieldIds;
  }

  imageNavigationFor = (fieldId) => {
    const ids = this.imageFieldIds();
    const index = ids.indexOf(fieldId);
    if(index < 0 || ids.length < 2) return null;
    return { hasPrev: index > 0, hasNext: index < ids.length - 1 };
  }

  handleImageNavigate = (fieldId, direction, view = null) => {
    const ids = this.imageFieldIds();
    const target = ids[ids.indexOf(fieldId) + (direction === 'next' ? 1 : -1)];
    if(target === undefined) return;
    this.pendingImageScrolls = 0;
    this.pendingScrollFieldId = target;
    this.setState({ openImageFieldId: target, openImageView: view }, () => {
      if(this.scrollFieldRowIntoView(target)) {
        this.pendingScrollFieldId = null;
      } else {
        this.settlePendingImageScroll();
      }
    });
  }

  handleImageDialogOpened = () => {
    this.setState({ openImageFieldId: null });
  }

  rowRenderer = ({ index, isScrolling, key, parent, style }) => {


    if (isScrolling) {
      return (
        <div
          key={key}
          style={{...style}}>
          Scrolling...
        </div>
      );
    }

    return (
      <CellMeasurer
        cache={this.cache}
        columnIndex={0}
        key={key}
        parent={parent}
        rowIndex={index}
      >
        {({ measure }) => {
          let firstSubfieldsId = this.visibleData.findIndex(x => x.type === 'subfields');

          return(
            <div style={{...style}}>
              <TranslationField
                {...this.visibleData[index]}
                itemId={this.props.itemId}
                isPending={this.props.isPending}
                from={this.props.from}
                to={this.props.to}
                rawData={this.visibleData[index].type == 'subfields' ? this.state.data : null}
                parentField={this.props.parentField ? this.props.parentField : null}
                forceUpdate={this.state.forceUpdate}
                forceOpen={this.props.forceOpen}
                isFirst={(index === firstSubfieldsId)}
                triggerClearAllTranslations={this.props.triggerClearAllTranslations}
                triggerDiscardChanges={this.props.triggerDiscardChanges}
                filterQuery={this.state.filterQuery}
                saveErrors={this.props.saveErrors}
                hideSourceToTargetMenu={this.props.hideSourceToTargetMenu}
                flagsOutdated={this.props.flagsOutdated}
                onKeepTranslation={this.props.onKeepTranslation}
                context={this.props.context}
                largeImages={this.props.largeImages}
                imageNavigation={this.imageNavigationFor(this.visibleData[index].id)}
                openImageDialog={this.state.openImageFieldId === this.visibleData[index].id}
                openImageDialogView={this.state.openImageView}
                imageBroken={this.state.brokenTargets.includes(this.visibleData[index].id)}
                onImageNavigate={this.handleImageNavigate}
                onImageDialogOpened={this.handleImageDialogOpened}

                onAddItem={this.props.onAddItem}
                onDeleteItem={this.props.onDeleteItem}
                onDeleteTranslation={this.props.onDeleteTranslation}
                onChange={this.props.onChange}
                onLoaded={measure}
                onExpandCollapsableFields={this.props.onExpandCollapsableFields}
                onFieldSelected={this.props.onFieldSelected}
                onSubfieldSelected={this.props.onSubfieldSelected}
                onSubfieldsExpand={this.props.onSubfieldsExpand}
                onSubfieldsExpanded={this.props.onSubfieldsExpanded}
                onSubfieldsCollapsed={this.props.onSubfieldsCollapsed}
                onTranslationModeChange={this.props.onTranslationModeChange}

                subfieldsPaginateFunc={this.props.subfieldsPaginateFunc}
              />
            </div>
          );
        }}
      </CellMeasurer>
    );
  }

  renderVirtualList = () => {
    const { t, data, scrollElement } = this.props;
    const { scrollToIndex } = this.state;

    return <div className="virtual-list" ref={el => { this._virtualListEl = el; }}>
      <WindowScroller
        ref={el => this._setWindowScrollerRef(el)}    
        scrollElement={document.querySelector(scrollElement ? scrollElement : '#mainFrame')}
        onResize={this.resizeList}
        onScroll={this.clearScrollToIndex}
      >  
        {({ height, registerChild, onChildScroll, scrollTop }) => (
            <AutoSizer disableHeight={true}>  
              {({ width }) => {
                return (
                  <div ref={el => registerChild(el)}>
                    <List
                      {...this.props}
                      ref={el => this._setListRef(el)}
                      style={{
                        padding: !data.length ? 0 : '24px 24px 0 24px',
                        outline: 'none',
                        overflow: 'visible',
                      }}
                      width={width}
                      height={height ? height : 0}
                      rowCount={this.visibleData.length}
                      deferredMeasurementCache={this.cache}
                      rowHeight={this.cache.rowHeight}
                      estimatedRowSize={100}
                      rowRenderer={this.rowRenderer.bind(this)}
                      onRowsRendered={this.onRowsRendered}
                      overscanRowCount={2}
                      autoHeight
                      scrollTop={scrollTop}
                      onScroll={onChildScroll}
                      scrollToAlignment="center"
                      scrollToIndex={scrollToIndex}
                    />
                  </div> 
                );
              }}
          </AutoSizer>
        )} 
      </WindowScroller>
    </div>
  }

  renderStaticList = () => {
    const { t, nested, itemId, gridItem, onChange, isPending, isReset, onExpandCollapsableFields } = this.props;
    const { data, filterQuery } = this.state;
    let firstSubfieldsId = this.visibleData.findIndex(x => x.type === 'subfields');

    return (<div 
        className={classNames('static-list', data.length == 0 && 'empty')} 
        ref={el => { this._staticListEl = el; }}
        //style={{ padding: (nested || data.length == 0) ? '0' : '0 24px' }}
      >
      {this.visibleData.map((n, i) => {
        //console.log(n)
          return (<TranslationField
            {...n}
            key={n.id}
            itemId={itemId}
            isPending={isPending}
            isSubfieldsPending={this.props.isSubfieldsPending}
            from={this.props.from}
            to={this.props.to}
            rawData={n.type == 'subfields' ? this.state.data : null}
            parentField={this.props.parentField}
            forceUpdate={this.state.forceUpdate}
            forceOpen={this.props.forceOpen}
            isFirst={(i === firstSubfieldsId)}
            triggerClearAllTranslations={this.props.triggerClearAllTranslations}
            triggerDiscardChanges={this.props.triggerDiscardChanges}
            filterQuery={this.state.filterQuery}
            saveErrors={this.props.saveErrors}
            hideSourceToTargetMenu={this.props.hideSourceToTargetMenu}
            flagsOutdated={this.props.flagsOutdated}
            onKeepTranslation={this.props.onKeepTranslation}
            context={this.props.context}
            largeImages={this.props.largeImages}
            imageNavigation={this.imageNavigationFor(n.id)}
            openImageDialog={this.state.openImageFieldId === n.id}
            openImageDialogView={this.state.openImageView}
            imageBroken={this.state.brokenTargets.includes(n.id)}
            onImageNavigate={this.handleImageNavigate}
            onImageDialogOpened={this.handleImageDialogOpened}

            onChange={onChange}
            onDeleteItem={this.props.onDeleteItem ? this.props.onDeleteItem : n.onDeleteItem}
            onDeleteTranslation={this.props.onDeleteTranslation ? this.props.onDeleteTranslation : n.onDeleteTranslation}
            onAddItem={this.props.onAddItem ? this.props.onAddItem : n.onAddItem}
            onExpandCollapsableFields={onExpandCollapsableFields}
            onFieldSelected={this.props.onFieldSelected}
            onSubfieldSelected={this.props.onSubfieldSelected}
            onSubfieldsExpand={this.props.onSubfieldsExpand}
            onSubfieldsExpanded={this.props.onSubfieldsExpanded}
            onSubfieldsCollapsed={this.props.onSubfieldsCollapsed}
            onTranslationModeChange={this.props.onTranslationModeChange}

            subfieldsPaginateFunc={this.props.subfieldsPaginateFunc}
          />)}
        )
      } 
    </div>)  
  }

  renderAutoTranslationBar = () => {
    if(!this.props.shop.shop.auto_translations.enabled || REACT_APP_ENABLE_AUTO_TRANSLATIONS == 'false' || this.props.to === 'IA' || window.location.href.indexOf('/notifications') > -1 || window.location.href.indexOf('/sms_notifications') > -1) return null;
    
    const { t, classes } = this.props;
    const { loadingTranslations, countingWords, autoTranslateMenuAnchor } = this.state;

    const isFreePlan = this.props.shop.shop.charge.name === 'free';
    // A save still writing holds fields the list shows as empty. Translating them again would
    // spend words on a second write racing the first, so the bar waits it out.
    const isSaving = this.props.isSaving === true;
    const hasEmptyFields = this.hasEmptyTranslatableFields();
    const emptyNum = this.countEmptyTranslatableFields();
    const overwritableNum = this.countOverwritableTranslations();
    const allTranslatableNum = this.countAllTranslatableFields();
    const outdatedNum = this.countOutdatedTranslations();
    const selection = this.state.translateSelection;
    const selectedNum = Object.keys(this.collectTranslatableStrings(selection)).length;
    const selectedReplaces = this.countReplaced(selection);
    // the button's icon says what the checklist would do; while the dialog is up, what is pending
    const showOverwriteIcon = this.state.translateDialog
      ? (this.modeSelection(this.pendingMode).filled || this.modeSelection(this.pendingMode).outdated)
      : selectedReplaces > 0;

    const usedAutoTranslationMargin = window.location.href.indexOf('/custom') > -1 ? 38 : 0;
    return <div data-tour="machine-translate-bar" className={classNames(classes.infoField, isFreePlan && classes.autoTranslateLockedBar)}>
      <span>
        {isSaving
          ? t('itemsList.autoTranslateBarSaving')
          : countingWords
            ? t('itemsList.autoTranslateBarCounting')
            : loadingTranslations
              ? t('itemsList.autoTranslateBarTranslating')
              : (hasEmptyFields ? t('itemsList.autoTranslateBarTitle') : t('itemsList.autoTranslateBarTitleAllTranslated'))}
      </span>
      {(loadingTranslations || isSaving) ?
        <CircularProgress size={24} className={classes.autoTranslationProgress} />
      : isFreePlan ?
        // no access at all: one plain button, room on the right for the lock icons of the hint
        <Button
          variant="outlined"
          size="small"
          disabled
          className={classes.autoTranslateLockedButton}
          style={{marginRight: usedAutoTranslationMargin + 51}}
        >
          <TranslateOutlined size={20} style={{marginRight: 8}} />
          {t('itemsList.autoTranslateBarButtonShort')}
        </Button>
      :
        <ButtonGroup
          variant="outlined"
          size="small"
          aria-label="machine translation split button"
          className={classes.autoTranslateButtonGroup}
          style={{marginRight: usedAutoTranslationMargin}}
        >
          <Button
            disabled={loadingTranslations || isFreePlan || isSaving}
            onClick={() => this.runAutoTranslation(selection)}
            style={{padding: '0 8px'}}
          >
            {showOverwriteIcon ?
              <PublishedWithChangesOutlined size={20} style={{marginRight: 8}} />
              :
              <TranslateOutlined size={20} style={{marginRight: 8}} />
            }
            <span className={classes.autoTranslateFullLabel}>{t('itemsList.autoTranslateBarButton', {wordsLeftNum: formatLargeNumber(this.props.shop.shop.auto_translations.available_words)})}</span>
            <span className={classes.autoTranslateShortLabel}>{t('itemsList.autoTranslateBarButtonShort')}</span>
          </Button>
          <Button
            disabled={loadingTranslations || isFreePlan || isSaving}
            onClick={this.openAutoTranslateMenu}
            aria-haspopup="menu"
            aria-label={t('itemsList.autoTranslateMoreOptions')}
            style={{padding: '0 2px'}}
          >
            <ArrowDropDownIcon />
          </Button>
        </ButtonGroup>
      }
      <Menu
        anchorEl={autoTranslateMenuAnchor}
        open={Boolean(autoTranslateMenuAnchor)}
        onClose={this.closeAutoTranslateMenu}
        anchorOrigin={{vertical: 'bottom', horizontal: 'right'}}
        transformOrigin={{vertical: 'top', horizontal: 'right'}}
        slotProps={{paper: {className: classes.autoTranslateMenuPaper}}}
      >
        {/* a checklist, not a list of actions: tick the kinds of fields the run should take, then
            run it. The menu stays open while ticking */}
        <MenuItem onClick={() => this.toggleTranslateSelection('empty')} disabled={emptyNum === 0}>
          <ListItemIcon>
            <Checkbox size="small" disableRipple tabIndex={-1} checked={selection.empty && emptyNum > 0} sx={{ p: 0 }} />
          </ListItemIcon>
          <ListItemText
            primary={t('itemsList.autoTranslateSelectEmpty', { count: emptyNum })}
            secondary={emptyNum === 0 ? t('itemsList.autoTranslateEmptyOptionEmptyHint') : null}
          />
        </MenuItem>
        {/* only in sections that flag outdated translations (the size chart today) */}
        {outdatedNum > 0 &&
          <MenuItem onClick={() => this.toggleTranslateSelection('outdated')}>
            <ListItemIcon>
              <Checkbox size="small" disableRipple tabIndex={-1} checked={selection.outdated} sx={{ p: 0 }} />
            </ListItemIcon>
            <ListItemText
              primary={t('itemsList.autoTranslateSelectOutdated', { count: outdatedNum })}
              secondary={t('itemsList.autoTranslateSelectOutdatedHint')}
            />
          </MenuItem>
        }
        <MenuItem onClick={() => this.toggleTranslateSelection('filled')} disabled={overwritableNum === 0}>
          <ListItemIcon>
            <Checkbox size="small" disableRipple tabIndex={-1} checked={selection.filled && overwritableNum > 0} sx={{ p: 0 }} />
          </ListItemIcon>
          <ListItemText
            primary={t('itemsList.autoTranslateSelectFilled', { count: overwritableNum })}
            secondary={overwritableNum === 0 ? t('itemsList.autoTranslateOverwriteOptionEmptyHint') : t('itemsList.autoTranslateSelectFilledHint')}
          />
        </MenuItem>
        <Divider />
        <MenuItem onClick={() => this.runAutoTranslation(selection)} disabled={selectedNum === 0}>
          <ListItemIcon>
            {selectedReplaces > 0 ? <PublishedWithChangesOutlined fontSize="small" color="primary" /> : <TranslateOutlined fontSize="small" color="primary" />}
          </ListItemIcon>
          <ListItemText
            primary={selectedNum === 0 ? t('itemsList.autoTranslateRunNothing') : t('itemsList.autoTranslateRunSelected', { count: selectedNum })}
            secondary={selectedReplaces > 0 ? t('itemsList.confirmTranslationOverwrite', { count: selectedReplaces }) : null}
            primaryTypographyProps={{ color: 'primary', fontWeight: 600 }}
          />
        </MenuItem>
        <Divider />
        {/* the way back after "don't show again" in the dialog; the menu stays open on toggle */}
        <MenuItem onClick={() => this.setTranslateDialogDismissed(!this.state.translateDialogDismissed)}>
          <ListItemIcon>
            <Checkbox size="small" disableRipple tabIndex={-1} checked={!this.state.translateDialogDismissed} sx={{ p: 0 }} />
          </ListItemIcon>
          <ListItemText
            primary={t('itemsList.autoTranslateDetailsOption')}
            secondary={t('itemsList.autoTranslateDetailsOptionHint')}
          />
        </MenuItem>
      </Menu>
      {isFreePlan &&
        <UpgradePlanHint neededPlan={'basic'} position="end" placement="bottom" top={6} isOverlay />
      }
    </div>
  }

  renderNoAutoTranslationsLeftBar = () => {
    const { t } = this.props;
    if(!this.props.shop.shop.auto_translations.enabled || REACT_APP_ENABLE_AUTO_TRANSLATIONS == 'false' || this.props.to === 'IA') return null;

    const { classes } = this.props;
    return <div className={classes.warningField}>
      <span>{t('itemsList.noAutoTranslateBarTitle')}</span> 
      <Button 
        variant="outlined" 
        size="small" 
        onClick={this.openRechargePlans} 
        style={{padding: '0 8px'}}
      >
          
          {t('itemsList.buyMoreWords')}
      </Button>
    </div>
  }

  renderFieldFilterBar = () => {
    const { t, classes } = this.props;
    const { filterQuery } = this.state;
    

    return <div style={{position: 'relative'}}>
      <InputBase
        style={{ padding: '8px 24px' }}
        onChange={this.onFilterChange}
        value={filterQuery}
        placeholder={t('itemsList.filterPlaceholderText')}
        fullWidth
      />
      {filterQuery &&
        <div className={classes.closeIcon}>
          <CloseIcon onClick={this.onResetQuery}/>
        </div>
      }
      <Divider />
      {(filterQuery && this.filteredData.length == 0) &&
        <div style={{padding: 24}}>
          <Typography variant="h5" align="center">
            {t('itemsList.noMatchesFound')}
          </Typography>
        </div>
      }
    </div>
  }

  // The cost block both machine translation dialogs share: fields, the cost as a red debit
  // like the words log's spend entries, then whatever the dialog has to add
  // The one dialog between the word count and the paid request. Replaces the former chain of
  // overwrite notice, cost confirmation, HTML notice and not-enough-words dialog. Layout: what
  // the run does, a word ledger, then the warnings as alerts
  // the one dialog between the word count and the paid request, shared with the size chart's
  // update run (MachineTranslationDialog); the ticks live in this.translateExcluded
  renderTranslateDialog = () => {
    const d = this.state.translateDialog;
    if(!d) return null;
    const toLanguage = (this.props.languages?.languages || []).find(x => x.id == this.props.toLanguageId);
    return (
      <MachineTranslationDialog
        data={d}
        excluded={this.translateExcluded}
        onToggleField={this.toggleTranslateField}
        recounting={this.state.translateRecounting}
        languageName={toLanguage?.name || null}
        iconKeys={ICON_KEY_CONTEXTS.includes(this.props.context)}
        hideKeys={NO_KEY_CONTEXTS.includes(this.props.context)}
        defaultFieldsOpen={this.state.translateDialogFieldsOpen}
        // "nothing to translate" is never waved through: the merchant has to see why
        dismissable={d.missingNum === 0 && d.overwriteNum === 0 && d.fieldsNum > 0}
        dismissed={this.state.translateDialogDismissed}
        onToggleDismissed={this.toggleTranslateDialogDismissed}
        onClose={this.closeTranslateDialog}
        onConfirm={this.confirmTranslateDialog}
        // with nothing to send, the other kinds of run are offered; counts from the same
        // collectors the dropdown's checklist shows
        alternatives={d.fieldsNum === 0 && !selectionOf(this.pendingMode).filled ? {
          filled: this.countOverwritableTranslations(),
          outdated: selectionOf(this.pendingMode).outdated ? 0 : this.countOutdatedTranslations(),
        } : null}
        onRunSelection={this.runAlternativeFromDialog}
        onTranslateAvailable={this.confirmTranslateDialog}
        onGetMoreWords={this.openRechargePlans}
      />
    );
  }
  
  render() {
    const { 
      t,
      nested,
      isPending,
      classes,
      allowAutoTranslation,
      allowFieldFiltering,
      filterGroup,
      type,
      id,
      parentField,
      itemId,
      paginated
    } = this.props;

    const {
      data, 
      filterQuery,
      loadingTranslations,
      loadingTranslationsError,
      showMessage,
      canAutoTranslate,
    } = this.state;

    // Use virtualized lists on the theme section and for subfields
    const virtualized = (
      !this._isShopifyContentPage() && 
      (type !== 'subfields' && id !== 'images') && 
      parentField !== 'json-editor'
    ); // data.length > 0; //

    this.syncFieldRanks(data);
    this.filteredData = applyFieldRanks(
      filterFields(data, filterGroup, filterQuery, itemId, this.props.searchFieldLabels),
      this.fieldRanks
    );

    this.visibleData = this.pageSlice(this.filteredData);

    return (
      <React.Fragment>
        {!nested &&
          <Divider />
        }
          
        {!this.props.isPending ?
          <React.Fragment>
            <RechargePlans
              visible={this.state.showRechargePlans}
              onClose={this.onCloseRechargePlans}
              onBuy={this.onBuyWords}
            />

            {(allowAutoTranslation && this.isAutoTranslationAvailable() && this.props.shop.shop.auto_translations.available_words > 0) &&
              this.renderAutoTranslationBar()
            }
            {(allowAutoTranslation && this.isAutoTranslationAvailable() && this.props.shop.shop.auto_translations.available_words <= 0 && this.props.shop.shop.charge.name !== 'free') &&
              this.renderNoAutoTranslationsLeftBar()
            }
            {allowFieldFiltering &&
              null //this.renderFieldFilterBar()
            }

            {this.state.brokenTargets.length > 0 &&
              <MuiAlert
                severity="warning"
                style={{ margin: '12px 24px' }}
                action={
                  <Button color="inherit" size="small" onClick={this.handleClearBrokenTranslations}>
                    {t('imageField.clearBrokenBtn')}
                  </Button>
                }
              >
                {t('imageField.brokenTranslationsWarning')}
              </MuiAlert>
            }
            {(data.length > 0 && virtualized) &&
              this.renderVirtualList()
            }
            {(data && !virtualized) &&
              this.renderStaticList()
            }
          </React.Fragment>
        :
          <ItemsListSkeleton
            rows={5}
            hideTopToolbar={true}
            hideBottomToolbar={true}
            showSpinner={this.props.showSkeletonSpinner}
            contained
          />
        }

        {(data.length === 0 && !isPending) &&
          <div>
            {
              this.props.emptyMessage ? (
                this.props.emptyMessage
              ) : (
                <div className={classes.noTranslationFieldsMsg}>{t('itemsList.noFieldsInSection')}</div>
              )
            }
          </div>
        }

        {this.renderTranslateDialog()}

        <Snackbar open={loadingTranslationsError} autoHideDuration={6000} onClose={() => this.setState({loadingTranslationsError: false})}>
          <MuiAlert onClose={() => this.setState({loadingTranslationsError: false})} severity="error" elevation={6} variant="filled">
          {showMessage ? showMessage : t('system.whoopsMessage')}
          </MuiAlert>
        </Snackbar>
      </React.Fragment>
    );
  }

  _setListRef = ref => {
    this.listRef = ref;
  };

  _setWindowScrollerRef = ref => {
    this.windowScrollerRef = ref;
  };

  _isShopifyContentPage = () => {
    for(var i=0; i < this.props.data.length; i++) {
      if(this.props.data[i]?.type === 'subfields') {
        return true;
        break;
      }
    }
    return false;
  }
}
TranslationFields.propTypes = {
};
const mapStateToProps = ({ shop, languages }) => ({
  shop: shop,
  languages: languages,
});
const mapDispatchToProps = dispatch =>
  bindActionCreators(
    {
      updateAutoTranslations,
    },
    dispatch
  )


const styles = theme => ({
  root: {
    //backgroundColor: '#ccc !important',
    //paddingLeft: 16,
    position: 'relative !important',
    margin: '0 !important',
  },
  textField: {
    margin: 0,
    padding: 0,
    backgroundColor: theme.palette.background.paper,
  },
  inputField: {
    padding: '12px 14px !important',
    fontSize: '14px !important',
    boxSizing: 'border-box',
  },
  targetField: {
    borderStyle: 'dashed !important',
  },
  /**
   * `&&` verdoppelt den Selektor und damit die Spezifität (0,2,0 statt 0,1,0).
   * Nötig, weil diese Regel per `classNames(classes.textField,
   * classes.disabledField)` über die Basisklasse gelegt wird: Emotion
   * schlüsselt Klassen über den REGELINHALT, und dieser hier ist identisch mit
   * `label` weiter unten. Beide teilen sich deshalb eine Klasse — und damit die
   * Sheet-Position des ersten Vorkommens. Solange die vor `textField` liegt,
   * verliert der Modifier still. Das `&&` macht das Ergebnis unabhängig von der
   * Reihenfolge im Objekt und hebt zugleich die Hash-Kollision auf.
   */
  disabledField: {
    '&&': {
      backgroundColor: theme.palette.surfaceRecessed,
    },
  },
  gridItem: {
    margin: '0 !important',
  },
  gridOdd: {
    [theme.breakpoints.down('md')]: {
      paddingBottom: '0 !important',
      '&>div': {
        marginBottom: 0,
        '& fieldset': {
          borderBottom: 'none',
          borderBottomLeftRadius: 0,
          borderBottomRightRadius: 0,
        }
      },
      '& .ly-editor>div': {
        marginBottom: '0 !important',
        '& .redactor-box': {
          borderBottom: 'none',
          borderBottomLeftRadius: '0 !important',
          borderBottomRightRadius: '0 !important',
        }
      },
    },
  },
  gridEven: {
    [theme.breakpoints.down('md')]: {
      paddingTop: '0 !important',
      '&>div': {
        marginTop: -1,
        '& fieldset': {
          borderTopLeftRadius: 0,
          borderTopRightRadius: 0,
        }
      },
      '& .ly-editor>div': {
        marginTop: -1,
        '& .redactor-box': {
          borderTopLeftRadius: '0 !important',
          borderTopRightRadius: '0 !important',
        }
      },
    },
  },
  arrow: {
    position: 'absolute',
    cursor: 'pointer',
    left: '50%',
    top: '50%',
    margin: '-15px 0 0 -11px',
    color: theme.palette.text.secondary,
    [theme.breakpoints.down('md')]: {
      display: 'none',
    },
    '& svg': {
      //fontSize: 22,
    }
  },
  noTranslationFieldsMsg: {
    textAlign: 'center',
    padding: '50px 0'
  },
  infoField: {
    position: 'relative',
    borderBottom: `solid 1px ${theme.palette.divider}`,
    padding: '8px 24px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    ...bannerSurface(theme, theme.palette.infoBar),
    '& span': {
      lineHeight: 2,
    },
    '& button': {
      color: 'inherit',
      borderColor: 'currentColor',
      whiteSpace: 'nowrap',
    },
    [theme.breakpoints.down('md')]: {
      flexDirection: 'column',
      alignItems: 'flex-start',
      gap: 8,
      '& button': {
        width: '100%',
      },
    },
  },
  warningField: {
    borderBottom: `solid 1px ${theme.palette.divider}`,
    padding: '8px 24px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    ...bannerSurface(theme, theme.palette.warningBar),
    '& span': {
      lineHeight: 2,
    },
    '& button': {
      color: 'inherit',
      borderColor: 'currentColor',
      whiteSpace: 'nowrap',
    },
    [theme.breakpoints.down('md')]: {
      flexDirection: 'column',
      alignItems: 'flex-start',
      gap: 8,
      '& button': {
        width: '100%',
      },
    },
  },
  autoTranslationProgress: {
    margin: 4,
    color: theme.palette.infoBar.text,
    borderColor: theme.palette.infoBar.text,
  },
  // The options carry a describing subline, so the menu items have to wrap instead of
  // relying on MenuItem's default `nowrap`.
  autoTranslateMenuPaper: {
    maxWidth: 380,
    '& li': {
      whiteSpace: 'normal',
      alignItems: 'flex-start',
    },
    '& li > div:first-of-type': {
      marginTop: 2,
    },
  },
  autoTranslateFullLabel: {
    [theme.breakpoints.down('md')]: {
      display: 'none',
    },
  },
  autoTranslateShortLabel: {
    display: 'none',
    [theme.breakpoints.down('md')]: {
      display: 'inline',
    },
  },
  autoTranslateLockedBar: {
    '& .upgrade-plan-hint > svg:first-of-type': {
      marginRight: 3,
    },
  },
  autoTranslateLockedButton: {
    padding: '0 8px',
    lineHeight: 2,
    [theme.breakpoints.down('md')]: {
      width: 'auto !important',
    },
  },
  // Keeps the split button usable on small screens where `infoField` stretches
  // every button to the full width, which would blow up the dropdown arrow.
  autoTranslateButtonGroup: {
    [theme.breakpoints.down('md')]: {
      width: '100%',
      '& button': {
        width: 'auto',
      },
      '& button:first-of-type': {
        flex: 1,
      },
    },
  },
  closeIcon: {
    width: theme.spacing(5),
    height: '100%',
    position: 'absolute',
    cursor: 'pointer',
    right: 4,
    top: 10
  },
  label: {
    backgroundColor: theme.palette.surfaceRecessed,
  }
});

export default withRouter(connect(
  mapStateToProps,
  mapDispatchToProps
)(withTranslation()(withStyles(styles)(TranslationFields))));